// What each "Learn more" on the home page's services section says. One layout (LearnMoreDialogComponent) shows
// all four, so they read alike. Step details and notes may hold <strong> for emphasis (rendered with [innerHTML]).

/** GET /stats (corpers.online's statsService): totals a visitor would find once signed up. */
export interface PublicStats {
  houses: number;
  items_for_sale: number;
  ppas: number;
  ppa_reviews: number;
  corp_members: number;
}

/** Below this, a number reads as "empty" rather than "busy" - so it isn't shown. */
export const MIN_STAT_TO_SHOW = 20;

export interface LearnMoreStep {
  title: string;
  detail: string;
}

/** Who you are, and what you'd do: a card to pick it, and its steps. */
export interface LearnMoreJourney {
  icon: string;
  label: string;
  summary: string;
  steps: LearnMoreStep[];
  note?: string;
}

/** Which illustrated example of the product the dialog shows (see learn-more-dialog.component.html). */
export type LearnMorePreview = 'house' | 'sale' | 'chat' | 'ppa';

export interface LearnMoreContent {
  icon: string;
  title: string;
  tagline: string; // the one claim
  preview: LearnMorePreview;
  stat?: { key: keyof PublicStats; label: string };
  journeys?: LearnMoreJourney[];
  features?: Array<{ icon: string; text: string }>;
  quote?: { text: string; detail?: string };
  // Where "Get started" goes after sign-up (sign-up's returnUrl); none means the default, the national chat
  cta: { label: string; afterSignup?: string };
}

/** In the order of the home page's cards - "Next" goes through them in this order. */
export const LEARN_MORE_TOPICS = ['accommodation', 'chat', 'buy-and-sell', 'ppas'] as const;
export type LearnMoreTopic = typeof LEARN_MORE_TOPICS[number];

export const isLearnMoreTopic = (value: unknown): value is LearnMoreTopic =>
  (LEARN_MORE_TOPICS as readonly unknown[]).includes(value);

const HAND_OVER_FEE_NOTE =
  "<strong>One ₦10,000 hand-over fee</strong>, paid to the outgoing corper - and only once you've decided to take the house. No agent fees.";

export const LEARN_MORE: Record<LearnMoreTopic, LearnMoreContent> = {
  accommodation: {
    icon: 'apartment',
    title: 'Accommodation & roommates',
    tagline: "Take over a corper's place near your PPA. No agents.",
    preview: 'house',
    stat: { key: 'houses', label: 'houses listed right now' },
    journeys: [
      {
        icon: 'luggage',
        label: "I'm moving in",
        summary: 'Take over a place near your PPA',
        steps: [
          { title: 'Find a place', detail: 'Browse houses near your PPA, listed by the corpers living in them.' },
          { title: 'Ask everything', detail: 'Message the outgoing occupant and ask all your questions.' },
          { title: 'Inspect it', detail: 'Visit the house and meet the landlord.' },
          { title: 'Pay the hand-over fee', detail: 'Decided to take it? Pay the outgoing corper <strong>₦10,000</strong> for their help.' },
          { title: 'Move in', detail: 'Pay the landlord directly and settle in.' },
        ],
        note: HAND_OVER_FEE_NOTE,
      },
      {
        icon: 'key',
        label: "I'm moving out",
        summary: 'Pass your place on, earn ₦10,000',
        steps: [
          { title: 'List your place', detail: 'Post it on Houses with photos, the rent and where it is.' },
          { title: 'Answer in chat', detail: 'Interested corpers message you - check your messages often.' },
          { title: 'Show them around', detail: 'Help with the inspection and introduce them to the landlord.' },
          { title: 'Get ₦10,000', detail: 'When they decide to take it, they pay you <strong>₦10,000</strong> before moving in.' },
        ],
      },
      {
        icon: 'group',
        label: 'Share a place',
        summary: 'Find a roommate, or a room',
        steps: [
          { title: 'List or look', detail: 'Have a house? List it as <strong>"Seeking a roommate"</strong>. Need a room? Look for houses marked that way.' },
          { title: 'Talk it through', detail: 'Ask your questions, and say what kind of roommate you want.' },
          { title: 'Meet and agree', detail: 'See the place together and make your arrangements directly.' },
        ],
        note: 'No external interference. Just one corper to another!',
      },
      {
        icon: 'volunteer_activism',
        label: 'I need a free lodge',
        summary: 'Stay free while you serve',
        steps: [
          { title: 'Find a free lodge', detail: 'Search Houses near your PPA for ones marked <strong>FREE</strong>.' },
          { title: 'Call ahead', detail: "Call the number on the listing and you'll be told how to get there." },
          { title: 'Arrive', detail: 'Someone will be waiting to help you settle in.' },
        ],
      },
    ],
    cta: { label: 'Find a place', afterSignup: '/houses' },
  },

  chat: {
    icon: 'chat',
    title: 'Chat with corp members',
    tagline: "Talk to corpers already serving where you're going.",
    preview: 'chat',
    stat: { key: 'corp_members', label: 'corp members already here' },
    features: [
      { icon: 'forum', text: 'Group chats for your state and LGA' },
      { icon: 'question_answer', text: 'Real answers from corpers who have been there' },
      { icon: 'security', text: 'Safety tips and local insight before you arrive' },
      { icon: 'chat', text: 'Direct messages with any corp member' },
    ],
    quote: {
      text: 'No one should navigate service year alone.',
      detail: "Advice on accommodation, the best places to eat, or just someone to talk to - there's a community waiting for you.",
    },
    cta: { label: 'Join the conversation' }, // sign-up already lands in the national chat
  },

  'buy-and-sell': {
    icon: 'sell',
    title: 'Buy & sell',
    tagline: 'Settle in for less - buy from the corpers who are leaving.',
    preview: 'sale',
    stat: { key: 'items_for_sale', label: 'items for sale right now' },
    journeys: [
      {
        icon: 'shopping_bag',
        label: "I'm buying",
        summary: 'Beds, fans, cookers and more',
        steps: [
          { title: 'Search near your PPA', detail: 'Filter by state and LGA to find what you need.' },
          { title: 'Chat the seller', detail: 'Ask about the item and agree on a price.' },
          { title: 'Pick it up', detail: 'Arrange pickup directly. <strong>No middleman.</strong>' },
        ],
      },
      {
        icon: 'storefront',
        label: "I'm selling",
        summary: 'Clear out before you leave',
        steps: [
          { title: 'Post it', detail: 'Add a photo, the price and its condition.' },
          { title: 'Answer buyers', detail: 'Corpers near you message you directly.' },
          { title: 'Mark it sold', detail: "Once it's gone, mark it sold so buyers know." },
        ],
      },
    ],
    cta: { label: 'Start buying & selling', afterSignup: '/sales' },
  },

  ppas: {
    icon: 'domain',
    title: 'Find & review PPAs',
    tagline: 'Know your PPA before your first day.',
    preview: 'ppa',
    stat: { key: 'ppa_reviews', label: 'PPA reviews by corpers' },
    journeys: [
      {
        icon: 'search',
        label: 'Find my PPA',
        summary: 'Know what to expect',
        steps: [
          { title: 'Search for it', detail: 'By name, state or LGA.' },
          { title: 'Read the reviews', detail: 'The work environment, how staff treat corpers, and what is expected.' },
          { title: 'Turn up prepared', detail: 'Know what you are walking into on day one.' },
        ],
      },
      {
        icon: 'rate_review',
        label: 'Review my PPA',
        summary: 'Help the next corper',
        steps: [
          { title: 'Find your PPA', detail: "Or add it, if it isn't listed yet." },
          { title: 'Rate it', detail: "Stars, and a few honest words on what it's really like." },
        ],
      },
    ],
    quote: { text: "One honest review can change someone's service year for the better." },
    cta: { label: 'Find your PPA', afterSignup: '/ppa' },
  },
};
